/**
 * @nanokart/tryon-studio-sdk
 * Moments API Service
 * Calls POST /api/v1/tryonstudio/moments
 */

import { ApiClient } from './client';
import { MomentResult } from '../../types/config';

export const MOMENT_TYPE_VALUES = [
  'creative_workday',
  'escape',
  'unwind',
  'after_hours',
  'gentle_celebration',
] as const;

export type MomentTypeValue = typeof MOMENT_TYPE_VALUES[number];

export interface MomentTypeDefinition {
  id: MomentTypeValue;
  name: string;
  description: string;
}

export const MOMENT_TYPE_DEFINITIONS: MomentTypeDefinition[] = [
  { id: 'creative_workday', name: 'Creative Workday', description: 'Office / professional setting' },
  { id: 'escape', name: 'Escape', description: 'Outdoor / travel vibe' },
  { id: 'unwind', name: 'Unwind', description: 'Casual / relaxed setting' },
  { id: 'after_hours', name: 'After Hours', description: 'Evening / social setting' },
  { id: 'gentle_celebration', name: 'Gentle Celebration', description: 'Festive / celebration mood' },
];

export interface MomentsStudioApiResponse {
  request_id: string;
  operation_id: string;
  status: string;
  image_url: string;
  result_image_url: string; // Alias for image_url
  moment_type: string;
  credits_consumed: number;
  latency_ms: number;
}

/**
 * Moments API Service for the TryOn Studio SDK
 */
export class MomentsApiService {
  constructor(private client: ApiClient) {}

  /**
   * Generate a lifestyle (Moments) image.
   * @param tryonKey  The tryon_key from a previous /tryonstudio/tryon response
   * @param momentType  One of the MOMENT_TYPE_VALUES
   * @param customPrompt  Optional custom style instruction
   */
  async generateMoment(
    tryonKey: string,
    momentType: string,
    customPrompt?: string
  ): Promise<MomentsStudioApiResponse> {
    const response = await this.client.post<MomentsStudioApiResponse>(
      '/api/v1/tryonstudio/moments',
      {
        tryon_key: tryonKey,
        moment_type: momentType,
        ...(customPrompt ? { custom_prompt: customPrompt } : {}),
      }
    );
    return response.data;
  }

  /**
   * Map API response to SDK MomentResult
   */
  static mapToMomentResult(
    response: MomentsStudioApiResponse,
    tryonCacheKey: string
  ): MomentResult {
    return {
      momentType: response.moment_type,
      resultImageUrl: response.result_image_url || response.image_url,
      tryonCacheKey,
      processingTimeMs: response.latency_ms ?? 0,
      timestamp: new Date().toISOString(),
    };
  }
}
